import { AxiosRequestConfig } from 'axios'

import { TenantStatus } from '@/enums/tenant-status.enum'
import { communityConfigService } from '@/services/community-config.service'
import communityMediaDefaultService from '@/services/community-media-default.service'
import { usesFsdsPlatformTenantConfig } from '@/utils/tenant.utils'

import { baseService } from './base.service'

export interface TenantCertificateCustomization {
  issuerName?: string
  description?: string
  skills?: string[]
  pdfTemplateUrl?: string
}

export interface TenantEmailBranding {
  logoUrl?: string
  platformName?: string
  primaryColor?: string
  contactUrl?: string
  socialLinks?: { label: string; url: string }[]
}

export interface TenantMediaDefaults {
  defaultAvatarUrl?: string
  defaultThumbnailUrl?: string
  defaultFaviconUrl?: string
  defaultLogoUrl?: string
  socialLinks?: {
    platform?: string
    label?: string
    url: string
  }[]
  senderName?: string
  senderEmail?: string
  primaryColor?: string
}

export interface TenantCustomization {
  customCSS?: string
  customHeadHTML?: string
  customBodyHTML?: string
  allowedDomains?: string[]
  certificate?: TenantCertificateCustomization
  emailBranding?: TenantEmailBranding
  mediaDefaults?: TenantMediaDefaults
  [key: string]: unknown
}

export type ClearableTenantCertificateCustomization = {
  [Key in keyof TenantCertificateCustomization]?: TenantCertificateCustomization[Key] | null
}

export type ClearableTenantEmailBranding = Omit<{
  [Key in keyof TenantEmailBranding]?: TenantEmailBranding[Key] | null
}, 'socialLinks'> & {
  socialLinks?: TenantEmailBranding['socialLinks'] | null
}

export type ClearableTenantMediaDefaults = Omit<
  {
    [Key in keyof TenantMediaDefaults]?: TenantMediaDefaults[Key] | null
  },
  'socialLinks'
> & {
  socialLinks?: TenantMediaDefaults['socialLinks'] | null
}

export interface UpdateMyCertificateConfigPayload {
  certificate?: ClearableTenantCertificateCustomization
  emailBranding?: ClearableTenantEmailBranding
}

export interface UpdateMyMediaDefaultsPayload {
  mediaDefaults?: ClearableTenantMediaDefaults
}

export interface TenantConfig {
  id: string
  name: string
  subdomain: string
  theme?: {
    primaryColor?: string
    secondaryColor?: string
    logo?: string
    [key: string]: unknown
  }
  customization?: TenantCustomization
  features?: string[]
  status?: TenantStatus | string
  ebEnvironmentUrl?: string
  amplifyAppUrl?: string
  albDnsName?: string
  applicationPort?: number
  // Provisioning stages - array of stage names/descriptions
  stages?: string[]
}

export interface CreateTenantDto {
  name: string
  subdomain: string
  theme?: Record<string, unknown>
  customization?: Record<string, unknown>
}

export interface UpdateTenantDto {
  name?: string
  subdomain?: string
  theme?: Record<string, unknown>
  customization?: Record<string, unknown>
  status?: TenantStatus | string
}

export interface DeleteTenantResponse {
  success: boolean
  tenantId: string
  status: TenantStatus
  message: string
}

class TenantService {
  private static instance: TenantService
  private readonly baseService = baseService

  public static getInstance(): TenantService {
    if (!TenantService.instance) {
      TenantService.instance = new TenantService()
    }
    return TenantService.instance
  }

  /**
   * Get tenant configuration by ID (public endpoint, no auth required)
   */
  async getTenantConfig(tenantId: string): Promise<TenantConfig> {
    try {
      const response = await this.baseService.getClient().get<TenantConfig>(`/tenants/${tenantId}/config`)
      return response.data
    } catch (error: unknown) {
      // Return default config if API fails
      return {
        id: tenantId,
        name: 'Default Tenant',
        subdomain: tenantId,
        theme: {
          primaryColor: '#007bff',
          secondaryColor: '#6c757d',
        },
        features: [],
        status: TenantStatus.ACTIVE,
      }
    }
  }

  /**
   * Get tenant configuration by subdomain.
   * Community hosts: eztech-be only (media defaults never from fsds-be).
   * FSDS hosts: fsds-be `/tenants/config/:subdomain`.
   */
  async getTenantBySubdomain(subdomain: string, hostname?: string | null): Promise<TenantConfig | null> {
    const host =
      hostname ?? (typeof window !== 'undefined' ? window.location.hostname : null)
    if (!usesFsdsPlatformTenantConfig(host)) {
      try {
        return await communityConfigService.getTenantConfigBySubdomain(subdomain)
      } catch {
        return null
      }
    }

    try {
      const response = await this.baseService.getClient().get<TenantConfig>(`/tenants/config/${subdomain}`)
      return response.data
    } catch {
      return null
    }
  }

  /**
   * Get all tenants (admin only)
   */
  async getAllTenants(
    params?: { pageNumber?: number; pageSize?: number; search?: string; searchFields?: string[] },
    config?: AxiosRequestConfig
  ): Promise<{ data: TenantConfig[]; meta: { totalPages: number; [key: string]: unknown } }> {
    const response = await this.baseService.getClient().get<{
      data: TenantConfig[]
      meta: { totalPages: number; [key: string]: unknown }
    }>('/tenants', { ...config, params })
    return response.data
  }

  /**
   * Get tenant by ID (admin only)
   */
  async getTenantById(id: string, config?: AxiosRequestConfig): Promise<TenantConfig> {
    const response = await this.baseService.getClient().get<TenantConfig>(`/tenants/${id}`, config)
    return response.data
  }

  /**
   * Create new tenant (admin only)
   */
  async createTenant(data: CreateTenantDto, config?: AxiosRequestConfig): Promise<TenantConfig> {
    const response = await this.baseService.getClient().post<TenantConfig>('/tenants', data, config)
    return response.data
  }

  /**
   * Update tenant (admin only)
   */
  async updateTenant(id: string, data: UpdateTenantDto, config?: AxiosRequestConfig): Promise<TenantConfig> {
    const response = await this.baseService.getClient().put<TenantConfig>(`/tenants/${id}`, data, config)
    return response.data
  }

  /**
   * Delete tenant (admin only)
   * Returns detailed deletion status including infrastructure and database deletion results
   */
  async deleteTenant(id: string, config?: AxiosRequestConfig): Promise<DeleteTenantResponse> {
    const response = await this.baseService.getClient().delete<DeleteTenantResponse>(`/tenants/${id}`, config)
    return response.data
  }

  async updateMyCertificateConfig(data: UpdateMyCertificateConfigPayload): Promise<TenantCustomization> {
    const response = await this.baseService.getClient().put<TenantCustomization>('/tenants/my/certificate-config', data)
    return response.data
  }

  async updateMyMediaDefaults(
    data: UpdateMyMediaDefaultsPayload,
    hostname?: string | null
  ): Promise<TenantCustomization> {
    const host =
      hostname ?? (typeof window !== 'undefined' ? window.location.hostname : null)

    if (usesFsdsPlatformTenantConfig(host)) {
      throw new Error('Default media is only configurable on community tenant sites (Edunodes).')
    }

    const mediaDefaults = await communityMediaDefaultService.updateForCurrentHost(data)
    return {
      mediaDefaults: {
        defaultAvatarUrl: mediaDefaults.defaultAvatarUrl ?? undefined,
        defaultThumbnailUrl: mediaDefaults.defaultThumbnailUrl ?? undefined,
        defaultFaviconUrl: mediaDefaults.defaultFaviconUrl ?? undefined,
        defaultLogoUrl: mediaDefaults.defaultLogoUrl ?? undefined,
        socialLinks: mediaDefaults.socialLinks ?? undefined,
        senderName: mediaDefaults.senderName ?? undefined,
        senderEmail: mediaDefaults.senderEmail ?? undefined,
        primaryColor: mediaDefaults.primaryColor ?? undefined,
      },
    }
  }
}

const tenantService = TenantService.getInstance()
export default tenantService
